"use client";

import { useQuery } from "@tanstack/react-query";
import { config } from "@/config/env";
import { useAuth } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { useMemo, useCallback } from "react";

/**
 * YouTube API helper — calls youtube endpoints directly.
 */
function useYouTubeApi() {
  const { getToken } = useAuth();
  const { i18n } = useTranslation();

  const apiCall = useCallback(
    async <T>(method: string, path: string, body?: unknown): Promise<T> => {
      const token = await getToken();
      const headers: Record<string, string> = {
        "Accept-Language": i18n.language,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      if (body) {
        headers["Content-Type"] = "application/json";
      }

      const response = await fetch(`${config.apiUrl}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Request failed" }));
        const err = new Error(error.message || `HTTP ${response.status}`) as Error & { code?: string };
        err.code = error.code;
        throw err;
      }

      return response.json() as Promise<T>;
    },
    [getToken, i18n.language]
  );

  return apiCall;
}

// --- Types matching backend ---

export interface YouTubeAccount {
  id: string;
  userId: string;
  youtubeChannelId: string;
  channelTitle: string;
  avatarUrl?: string;
  connectedAt: string;
  updatedAt: string;
}

export interface YouTubePost {
  id: string;
  userId: string;
  jobId: string;
  youtubeAccountId: string;
  youtubeVideoId?: string;
  title: string;
  description: string;
  tags: string[];
  privacyStatus: "public" | "private" | "unlisted";
  categoryId: string;
  isAiGenerated: boolean;
  status: "pending" | "uploading" | "published" | "scheduled" | "failed";
  errorMessage?: string;
  scheduledAt?: string;
  publishedAt?: string;
  youtubeUrl?: string;
  createdAt: string;
}

/**
 * Fetch connected YouTube channels for the current user.
 */
export function useYouTubeAccounts() {
  const apiCall = useYouTubeApi();

  return useQuery({
    queryKey: ["youtube-accounts"],
    queryFn: () =>
      apiCall<{ accounts: YouTubeAccount[] }>("GET", "/youtube/accounts"),
    staleTime: 30_000,
  });
}

/**
 * Fetch YouTube posts for a specific video job with smart polling.
 * Polls every 3s while any post is still processing.
 */
export function useYouTubePosts(jobId: string | undefined) {
  const apiCall = useYouTubeApi();

  return useQuery({
    queryKey: ["youtube-posts", jobId],
    queryFn: () =>
      apiCall<{ posts: YouTubePost[] }>("GET", `/youtube/posts/by-job/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const posts = query.state.data?.posts;
      if (!posts) return false;
      const hasActive = posts.some(
        (p) => p.status === "pending" || p.status === "uploading"
      );
      return hasActive ? 3000 : false;
    },
  });
}

/**
 * YouTube API mutations (non-hooks, for use in callbacks).
 */
export function useYouTubeActions() {
  const apiCall = useYouTubeApi();

  return useMemo(
    () => ({
      getAuthUrl: () =>
        apiCall<{ authUrl: string }>("GET", "/youtube/auth/url"),

      handleCallback: (code: string, state: string) =>
        apiCall<{ success: boolean; accountId: string; channelTitle: string }>(
          "POST",
          "/youtube/auth/callback",
          { code, state }
        ),

      disconnect: (accountId: string) =>
        apiCall<{ success: boolean }>("DELETE", `/youtube/accounts/${accountId}`),

      publish: (params: {
        jobId: string;
        accountId: string;
        title: string;
        description?: string;
        tags?: string[];
        privacyStatus: string;
        categoryId?: string;
        isAiGenerated?: boolean;
        scheduledAt?: string;
      }) =>
        apiCall<{ postId: string; status: string }>("POST", "/youtube/publish", params),

      getPost: (postId: string) =>
        apiCall<{ post: YouTubePost }>("GET", `/youtube/posts/${postId}`),
    }),
    [apiCall]
  );
}
